import { useCallback } from 'react'
import type {
  FilterValue,
  NumberFilterValue,
  UseNumberRangeFilterReturn,
} from '../types/data-table.types'

export function useNumberRangeFilter(
  value: NumberFilterValue | undefined,
  onChange: (value: FilterValue) => void,
): UseNumberRangeFilterReturn {
  const handleMinChange = useCallback(
    (newMin: string) => {
      const min = newMin || undefined
      const max = value?.max
      const minNum = min ? Number(min) : null
      const maxNum = max ? Number(max) : null

      // Prevent setting min greater than max
      if (minNum !== null && maxNum !== null && minNum > maxNum) {
        return
      }

      const newValue = {
        ...(value || {}),
        min,
      }

      if (!newValue.min && !newValue.max) {
        onChange(undefined)
      } else {
        onChange(newValue)
      }
    },
    [value, onChange],
  )

  const handleMaxChange = useCallback(
    (newMax: string) => {
      const max = newMax || undefined
      const min = value?.min
      const minNum = min ? Number(min) : null
      const maxNum = max ? Number(max) : null

      // Prevent setting max less than min
      if (minNum !== null && maxNum !== null && minNum > maxNum) {
        return
      }

      const newValue = {
        ...(value || {}),
        max,
      }

      // If both min and max are now undefined, clear the entire filter
      if (!newValue.min && !newValue.max) {
        onChange(undefined)
      } else {
        onChange(newValue)
      }
    },
    [value, onChange],
  )

  const clearMin = useCallback(() => {
    const newValue = {
      ...(value || {}),
      min: undefined,
    }

    if (!newValue.min && !newValue.max) {
      onChange(undefined)
    } else {
      onChange(newValue)
    }
  }, [value, onChange])

  const clearMax = useCallback(() => {
    const newValue = {
      ...(value || {}),
      max: undefined,
    }

    if (!newValue.min && !newValue.max) {
      onChange(undefined)
    } else {
      onChange(newValue)
    }
  }, [value, onChange])

  const hasValidationError = Boolean(
    value?.min && value?.max && Number(value.min) > Number(value.max),
  )

  return {
    handleMinChange,
    handleMaxChange,
    clearMin,
    clearMax,
    hasValidationError,
  }
}
